import { ref, computed, type Ref } from 'vue'

type ValidationFunction<T> = (value: T) => string

type ValidationSchema = {
  [K: string]: ValidationFunction<any>
}

// Helper type to extract the parameter type from a validation function
type InferValidationType<T> = T extends ValidationFunction<infer U> ? U : never

// Helper type for the form values that uses reactive properties
type FormValues<T extends ValidationSchema> = {
  [K in keyof T]: InferValidationType<T[K]>
}

type TouchedState<T extends ValidationSchema> = {
  [K in keyof T]: boolean
}

type ValidationErrors<T extends ValidationSchema> = {
  [K in keyof T]: string
}

export function useFormValidation<T extends ValidationSchema>(
  schema: T,
  formValues: FormValues<T>,
) {
  const touched = ref<TouchedState<T>>({} as TouchedState<T>)
  const submitting = ref(false)

  // Initialize touched state for all fields
  Object.keys(schema).forEach((field) => {
    touched.value[field as keyof T] = false
  })

  const validate = <K extends keyof T>(
    field: K,
    value: InferValidationType<T[K]>,
  ) => {
    if (!touched.value[field] && !submitting.value) return ''
    return schema[field](value)
  }

  const errors = computed(() => {
    const result = {} as ValidationErrors<T>
    Object.keys(schema).forEach((field) => {
      const key = field as keyof T
      result[key] = validate(key, formValues[key])
    })
    return result
  })

  const validateAll = () => {
    const result = {} as ValidationErrors<T>
    Object.keys(schema).forEach((field) => {
      const key = field as keyof T
      result[key] = schema[key](formValues[key])
    })
    return result
  }

  const isValid = computed(() => {
    // If not submitting, only validate touched fields
    if (!submitting.value) {
      const touchedFields = Object.keys(schema).filter(
        (field) => touched.value[field as keyof T],
      )
      if (touchedFields.length === 0) return true // No fields touched yet
      return !touchedFields.some((field) => {
        const key = field as keyof T
        return schema[key](formValues[key]) !== ''
      })
    }
    // When submitting, validate all fields
    return !Object.values(validateAll()).some((error) => error !== '')
  })

  const handleFieldTouch = (field: keyof T) => {
    touched.value[field] = true
  }

  const markAllFieldsTouched = () => {
    Object.keys(schema).forEach((field) => {
      touched.value[field as keyof T] = true
    })
  }

  const getInputClass = (field: keyof T) => {
    if (!touched.value[field] && !submitting.value) return ''
    return errors.value[field]
      ? 'border-red-500 focus:ring-red-500'
      : 'border-green-500 focus:ring-green-500'
  }

  const setSubmitting = (value: boolean) => {
    submitting.value = value
  }

  return {
    touched,
    submitting,
    errors,
    isValid,
    handleFieldTouch,
    markAllFieldsTouched,
    getInputClass,
    setSubmitting,
  }
}
